/**
 * ClientDetailHeader Component (Story 3.8, AC #1)
 *
 * Displays client header with avatar, name, tags, and action buttons.
 * Handles archived state: shows "Archived" badge, hides edit/archive,
 * shows restore button with plan limit check.
 *
 * Layout follows industry-standard SaaS detail-page convention (Linear/Stripe/Attio):
 * full-bleed flush-left header, primary action solid, secondary outlined,
 * destructive actions tucked inside an overflow menu.
 */

import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import {
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@provenleap/ui";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { motion, AnimatePresence } from "motion/react";
import {
  PencilSimple,
  Archive,
  ArrowCounterClockwise,
  Trash,
  DotsThreeVertical,
  FilePlus,
  User,
  EnvelopeSimple,
  Building,
  Buildings,
  Tag,
  Briefcase,
  Copy,
  Check,
  AddressBook,
} from "@phosphor-icons/react";
import { apiGet, apiPatch } from "../../lib/apiClient";
import { usePermission, Actions } from "../../hooks/usePermission";
import { useUpgradeGuard } from "../../hooks/useUpgradeGuard";
import type { ToastType } from "../../hooks/useToast";
import { ClientAvatar } from "./ClientAvatar";
import { AnalyzeWithAiButton } from "../ai-access/AnalyzeWithAiButton";
import { TagPill } from "./TagPill";
import { getIndustryIcon } from "./IndustryPicker";
import { OverageAddConfirmDialog } from "./OverageAddConfirmDialog";
import type { Client } from "./types";

type ClientDetailHeaderVariant = "default" | "left" | "right";
type RestoreAttempt = Pick<Client, "id" | "name" | "updatedAt">;

interface ClientDetailHeaderProps {
  client: Client;
  onBack: () => void;
  onEditClick: (triggerEl?: HTMLButtonElement | null) => void;
  onDeleteClick: () => void;
  onPrimaryAction?: () => void;
  showToast: (type: ToastType, message: string) => void;
  /**
   * "default" — full row (avatar + name + actions).
   * "left"    — only back button + avatar + name + tags.
   * "right"   — only action buttons (Edit/Restore + kebab) + dialogs.
   */
  variant?: ClientDetailHeaderVariant;
}

export function ClientDetailHeader({
  client,
  onBack: _onBack,
  onEditClick,
  onDeleteClick,
  onPrimaryAction,
  showToast,
  variant = "default",
}: ClientDetailHeaderProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission } = usePermission();
  const canManageClients = hasPermission(Actions.MANAGE_CLIENTS);
  const canManageBilling = hasPermission(Actions.MANAGE_BILLING);

  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [overageRestoreTarget, setOverageRestoreTarget] =
    useState<RestoreAttempt | null>(null);
  const [overageRestoreOpen, setOverageRestoreOpen] = useState(false);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [infoOpen, setInfoOpen] = useState(false);

  function handleCopy(field: string, value: string) {
    navigator.clipboard.writeText(value).then(() => {
      setCopiedField(field);
      setTimeout(
        () => setCopiedField((current) => (current === field ? null : current)),
        1500,
      );
    });
  }

  // Active client count for upgrade guard
  const { data: activeClients } = useQuery<Client[]>({
    queryKey: ["clients"],
    queryFn: async () => {
      const res = await apiGet("/api/clients");
      if (!res.ok) throw new Error("Failed to load clients");
      const json = await res.json();
      return json.data;
    },
    staleTime: 5 * 60 * 1000,
  });
  // Exclude demo clients from plan limit count (Story ST.1, AC#4)
  const activeClientCount =
    activeClients?.filter((c) => !c.demoMode)?.length ?? 0;

  const { guardAction, UpgradeModal, showUpgradeModal } = useUpgradeGuard({
    feature: "clientLimit",
    currentCount: activeClientCount,
    serverAuthoritative: true,
  });

  const isArchived = !!client.archivedAt;

  function beginOverageRestore(target: RestoreAttempt): void {
    if (!canManageBilling) {
      showToast(
        "error",
        "An account owner must review and approve the overage before this client can be restored.",
      );
      return;
    }
    // Preserve the exact client that received the server rejection even if the
    // detail route changes before the quote/confirm round trip completes.
    setOverageRestoreTarget(target);
    setOverageRestoreOpen(true);
  }

  function handleOverageRestoreOpenChange(next: boolean): void {
    setOverageRestoreOpen(next);
    if (!next) setOverageRestoreTarget(null);
  }

  // Archive mutation
  const archiveClient = useMutation({
    mutationFn: async () => {
      const res = await apiPatch(`/api/clients/${client.id}/archive`, {
        updatedAt: client.updatedAt,
      });
      const json = await res.json().catch(() => ({ detail: "Server error" }));
      if (!res.ok) throw { status: res.status, ...json };
      return json;
    },
    onSuccess: () => {
      setArchiveDialogOpen(false);
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["clients", client.id] });
      navigate({ to: "/clients", replace: true });
    },
    onError: (err: any) => {
      setArchiveDialogOpen(false);
      if (err?.type?.includes?.("already-archived")) {
        showToast("info", "Client is already archived");
      } else {
        showToast(
          "error",
          err?.title ?? err?.detail ?? "Failed to archive client",
        );
      }
    },
  });

  // Restore mutation
  const restoreClient = useMutation({
    mutationFn: async (target: RestoreAttempt) => {
      const res = await apiPatch(`/api/clients/${target.id}/restore`, {
        updatedAt: target.updatedAt,
      });
      const json = await res.json().catch(() => ({ detail: "Server error" }));
      if (!res.ok) throw { status: res.status, ...json };
      return json;
    },
    onSuccess: (_data, target) => {
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["clients", target.id] });
      showToast("success", "Client restored");
    },
    onError: (err: any, target) => {
      if (err?.type?.includes?.("overage-confirmation-required")) {
        beginOverageRestore(target);
      } else if (err?.type?.includes?.("upgrade-required")) {
        showUpgradeModal();
      } else if (err?.type?.includes?.("not-archived")) {
        showToast("info", "Client is not archived");
      } else {
        showToast(
          "error",
          err?.title ?? err?.detail ?? "Failed to restore client",
        );
      }
    },
  });

  function handleRestore() {
    const target: RestoreAttempt = {
      id: client.id,
      name: client.name,
      updatedAt: client.updatedAt,
    };
    guardAction(async () => {
      restoreClient.mutate(target);
    });
  }

  function handleOverageRestoreConfirmed(): void {
    const restoredClientId = overageRestoreTarget?.id;
    void queryClient.invalidateQueries({ queryKey: ["clients"] });
    if (restoredClientId) {
      void queryClient.invalidateQueries({
        queryKey: ["clients", restoredClientId],
      });
    }
    setOverageRestoreOpen(false);
    setOverageRestoreTarget(null);
    showToast("success", "Client restored");
  }

  const leftSection = (
    <div className="flex items-center gap-3">
      <ClientAvatar
        name={client.name}
        logoUrl={client.logoUrl}
        size="md"
        className="!h-12 !w-12 !text-base"
      />

      {isArchived && (
        <span className="bg-neutral-100 text-neutral-500 text-xs rounded-full px-2 py-0.5 shrink-0">
          Archived
        </span>
      )}

      <div className="relative">
        <Button
          type="button"
          variant="ghost"
          className="rounded-xl bg-background cursor-pointer shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all text-[13px]"
          style={{ border: "0.5px solid hsl(var(--input))" }}
          onClick={() => setInfoOpen((open) => !open)}
        >
          <AddressBook size={15} className="mr-1.5" />
          Client Info
        </Button>

        <AnimatePresence>
          {infoOpen && (
            <>
              {/* Backdrop — click outside to close */}
              <div
                className="fixed inset-0 z-40"
                onClick={() => setInfoOpen(false)}
              />

              {/* Popover panel */}
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: -4 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: -4 }}
                transition={{ duration: 0.15, ease: "easeOut" }}
                className="absolute left-0 top-full mt-2.5 z-50 w-[320px] bg-popover rounded-xl shadow-md p-4"
                style={{ border: "0.5px solid hsl(var(--input))" }}
              >
                {/* Arrow pointing up */}
                <div
                  className="absolute -top-[5px] left-5 w-2.5 h-2.5 bg-popover rotate-45 z-10"
                  style={{
                    borderLeft: "0.5px solid hsl(var(--input))",
                    borderTop: "0.5px solid hsl(var(--input))",
                  }}
                />

                <div className="flex flex-col text-[13px] text-foreground divide-y divide-input/30">
                  <ClientInfoRow
                    icon={Building}
                    label="Name"
                    value={client.name}
                    copied={copiedField === "name"}
                    onCopy={() => handleCopy("name", client.name)}
                  />

                  <ClientInfoRow
                    icon={User}
                    label="Contact name"
                    value={client.contactName ?? null}
                    copied={copiedField === "contactName"}
                    onCopy={
                      client.contactName
                        ? () =>
                            handleCopy(
                              "contactName",
                              client.contactName as string,
                            )
                        : undefined
                    }
                  />

                  <ClientInfoRow
                    icon={EnvelopeSimple}
                    label="Contact email"
                    value={client.contactEmail ?? null}
                    copied={copiedField === "contactEmail"}
                    onCopy={
                      client.contactEmail
                        ? () =>
                            handleCopy(
                              "contactEmail",
                              client.contactEmail as string,
                            )
                        : undefined
                    }
                    breakAll
                  />

                  <ClientInfoRow
                    icon={
                      (client.industry && getIndustryIcon(client.industry)) ||
                      Briefcase
                    }
                    label="Industry"
                    value={client.industry ?? null}
                    copied={copiedField === "industry"}
                    onCopy={
                      client.industry
                        ? () =>
                            handleCopy("industry", client.industry as string)
                        : undefined
                    }
                  />

                  {/* Brand — read-only here (shown only when assigned); the brand is
                set/changed in the Edit Client form (Epic 40). */}
                  {client.brand && (
                    <ClientInfoRow
                      icon={Buildings}
                      label="Brand"
                      value={client.brand.name}
                      copied={copiedField === "brand"}
                      onCopy={() => handleCopy("brand", client.brand!.name)}
                    />
                  )}

                  <div className="flex items-center gap-2.5 pt-4 pb-2.5">
                    <Tag size={18} className="shrink-0 text-foreground" />
                    <div className="min-w-0 flex-1">
                      {client.tags && client.tags.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {client.tags.map((tag) => (
                            <TagPill key={tag.id} tag={tag} size="sm" />
                          ))}
                        </div>
                      ) : (
                        <span className="text-foreground/45">—</span>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </div>
  );

  const rightSection = (
    <div className="flex items-center gap-2 shrink-0">
      {/* Story 29-17 AC#13 — "Analyze with AI" entry point. Self-gates to null until the
          MCP connect flag is flipped on (renders nothing pre-launch). */}
      {!isArchived && <AnalyzeWithAiButton />}
      {!isArchived && canManageClients && onPrimaryAction && (
        <Button
          type="button"
          variant="brand"
          className="rounded-xl cursor-pointer"
          onClick={onPrimaryAction}
        >
          <FilePlus size={15} className="mr-1.5" weight="duotone" />
          Generate Report
        </Button>
      )}
      {!isArchived && canManageClients && (
        <Button
          type="button"
          variant="ghost"
          className="rounded-xl bg-background cursor-pointer shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all text-[13px]"
          style={{ border: "0.5px solid hsl(var(--input))" }}
          onClick={(e) => onEditClick(e.currentTarget)}
        >
          <PencilSimple size={15} className="mr-1.5" />
          Edit
        </Button>
      )}
      {isArchived && canManageClients && (
        <Button
          type="button"
          variant="ghost"
          className="rounded-xl bg-background cursor-pointer shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all text-[13px]"
          style={{ border: "0.5px solid hsl(var(--input))" }}
          onClick={handleRestore}
          disabled={restoreClient.isPending}
        >
          <ArrowCounterClockwise size={15} className="mr-1.5" />
          {restoreClient.isPending ? "Restoring..." : "Restore Client"}
        </Button>
      )}
      {canManageClients && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              className="rounded-xl bg-background cursor-pointer shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all px-3"
              style={{ border: "0.5px solid hsl(var(--input))" }}
              aria-label="More actions"
            >
              <DotsThreeVertical size={18} weight="bold" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="rounded-xl p-1.5 shadow-md"
            style={{ border: "0.5px solid hsl(var(--input))" }}
          >
            {!isArchived && (
              <DropdownMenuItem
                className="min-h-9 text-[13px] cursor-pointer rounded-lg"
                onClick={() => setArchiveDialogOpen(true)}
              >
                <Archive size={14} className="mr-2" />
                Archive
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="min-h-9 text-[13px] text-destructive cursor-pointer rounded-lg focus:text-destructive"
              onClick={onDeleteClick}
              data-testid="delete-client-btn"
            >
              <Trash size={14} weight="duotone" className="mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );

  return (
    <>
      {variant === "left" ? (
        leftSection
      ) : variant === "right" ? (
        rightSection
      ) : (
        <div className="flex items-center gap-4">
          {leftSection}
          <div className="flex-1" />
          {rightSection}
        </div>
      )}

      {/* Archive confirmation dialog */}
      <DialogPrimitive.Root
        open={archiveDialogOpen}
        onOpenChange={setArchiveDialogOpen}
      >
        <AnimatePresence>
          {archiveDialogOpen && (
            <DialogPrimitive.Portal forceMount>
              <DialogPrimitive.Overlay asChild forceMount>
                <motion.div
                  className="fixed inset-0 z-50 bg-black/35"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.25, ease: "easeOut" }}
                />
              </DialogPrimitive.Overlay>

              <DialogPrimitive.Content asChild forceMount>
                <motion.div
                  className="fixed left-1/2 top-1/2 z-50 w-full max-w-[calc(100vw-2rem)] sm:max-w-[400px] -translate-x-1/2 -translate-y-1/2 rounded-3xl border-[0.5px] border-input bg-background p-8 shadow-lg outline-none"
                  initial={{ opacity: 0, scale: 0.95, y: 10 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.95, y: 10 }}
                  transition={{ duration: 0.25, ease: "easeOut" }}
                >
                  <div className="flex flex-col space-y-1.5 text-left">
                    <DialogPrimitive.Title className="text-lg font-semibold leading-none tracking-tight">
                      Archive {client.name}?
                    </DialogPrimitive.Title>
                    <DialogPrimitive.Description className="text-sm text-muted-foreground">
                      Their data will be preserved but hidden from your main
                      list. Archived clients don&apos;t count toward your plan
                      limit.
                    </DialogPrimitive.Description>
                  </div>

                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                  >
                    <div className="flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2 mt-6">
                      <Button
                        variant="ghost"
                        className="rounded-xl bg-background cursor-pointer shadow-xs hover:-translate-y-0.5 hover:shadow-sm hover:bg-background transition-all"
                        style={{ border: "0.5px solid hsl(var(--input))" }}
                        onClick={() => setArchiveDialogOpen(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="destructive"
                        className="rounded-xl cursor-pointer"
                        onClick={() => archiveClient.mutate()}
                        disabled={archiveClient.isPending}
                        data-testid="confirm-archive-btn"
                      >
                        <Archive size={16} className="mr-1.5" />
                        {archiveClient.isPending ? "Archiving..." : "Archive"}
                      </Button>
                    </div>
                  </motion.div>
                </motion.div>
              </DialogPrimitive.Content>
            </DialogPrimitive.Portal>
          )}
        </AnimatePresence>
      </DialogPrimitive.Root>

      <OverageAddConfirmDialog
        mode="restore"
        restoreClient={
          overageRestoreTarget
            ? {
                id: overageRestoreTarget.id,
                name: overageRestoreTarget.name,
              }
            : null
        }
        open={overageRestoreOpen && overageRestoreTarget !== null}
        onOpenChange={handleOverageRestoreOpenChange}
        onConfirmed={handleOverageRestoreConfirmed}
      />

      {UpgradeModal}
    </>
  );
}

interface ClientInfoRowProps {
  icon: React.ComponentType<{ size?: number; className?: string }>;
  label: string;
  value: string | null;
  copied: boolean;
  onCopy?: () => void;
  breakAll?: boolean;
}

function ClientInfoRow({
  icon: Icon,
  label,
  value,
  copied,
  onCopy,
  breakAll,
}: ClientInfoRowProps) {
  return (
    <div className="flex items-center gap-2.5 py-2.5">
      <Icon size={18} className="shrink-0 text-foreground" />
      <div
        className={`flex-1 min-w-0 ${breakAll ? "break-all" : "break-words"}`}
      >
        {value || <span className="text-foreground/45">—</span>}
      </div>
      {onCopy && (
        <button
          type="button"
          onClick={onCopy}
          aria-label={`Copy ${label.toLowerCase()}`}
          className="shrink-0 p-1.5 cursor-pointer"
        >
          {copied ? (
            <Check size={17} className="shrink-0 text-emerald-600" />
          ) : (
            <Copy
              size={17}
              className="shrink-0 text-foreground/55 hover:text-foreground transition-colors"
            />
          )}
        </button>
      )}
    </div>
  );
}
